/**
 * PfdEditor — Wave CG.4 / CH.1 · Interactive PFD Editor (V2)
 *
 * Capabilities:
 *   • Pan + zoom (mouse wheel / drag canvas)
 *   • Drag equipment to reposition (snap-to-grid)
 *   • Click two nozzles in "Add Line" mode to create a stream
 *   • Drag equipment from palette onto canvas to add new equipment
 *   • Undo / redo (Ctrl/Cmd-Z, Ctrl/Cmd-Shift-Z or Ctrl/Cmd-Y)
 *   • Click an equipment or stream → properties panel
 *   • Delete selection (Del key or toolbar button)
 *   • Edit tag / label / line_no / fluid in properties panel
 *   • Fullscreen toggle (toolbar button or F11-like behaviour)
 *   • Save → backend re-renders SVG and bumps revision
 */
import React, { useCallback, useEffect, useMemo, useRef, useState }
   from "react";
import api, { formatErr } from "../../lib/api";
import { Loader2, Save, MousePointer2, Link2, Trash2, AlertCircle,
   ZoomIn, ZoomOut, RotateCcw, Undo2, Redo2, Maximize2, Minimize2,
   X as XIcon } from "lucide-react";
import {
   GRID, COL, mono, snap, newId,
   screenToSvg as toSvg, resolveNozzle, orthogonalPath,
   routeAroundObstacles,
   defaultNozzlesFor, generateTag, EQUIPMENT_PALETTE,
} from "./engineering_canvas_lib";
import EquipmentPalette from "./EquipmentPalette";
import EquipmentSymbol from "./EquipmentSymbol";
import { useEditorHistory } from "./useEditorHistory";

const PID_EXTRA = [
   { type: "valve",  label: "Valve",  tag_prefix: "FV" },
   { type: "filter", label: "Filter", tag_prefix: "F"  },
];

export default function PfdEditor(props) {
   return <BaseEngineeringEditor {...props} docKind="pfd"/>;
}

/**
 * Reusable editor shell — drives both PFD and P&ID editors.
 * docKind = "pfd" | "pid" controls the endpoint + testid + title.
 */
export function BaseEngineeringEditor({
   projectId, sheetId, sheetTitle, onSaved,
   docKind = "pfd",
   onRequestClose,           // optional — show a Close button when set
}) {
   const titlePrefix = docKind === "pid" ? "P&ID EDITOR" : "PFD EDITOR";
   const testidPrefix = docKind === "pid" ? "pid-editor" : "pfd-editor";
   const extraPaletteItems = docKind === "pid" ? PID_EXTRA : [];

   const [doc, setDoc] = useState(null);
   const [loading, setLoading] = useState(false);
   const [saving, setSaving] = useState(false);
   const [err, setErr] = useState("");
   const [tool, setTool] = useState("select");
   const [selection, setSelection] = useState(null);
   const [multiSelect, setMultiSelect] = useState([]);   // array of eq ids
   const [linkFrom, setLinkFrom] = useState(null);
   const [zoom, setZoom] = useState(1);
   const [pan, setPan] = useState({ x: 0, y: 0 });
   const [dragEq, setDragEq] = useState(null);
   const [isPanning, setIsPanning] = useState(false);
   const [draggingType, setDraggingType] = useState(null);
   const [fullscreen, setFullscreen] = useState(false);
   const [rubberBand, setRubberBand] = useState(null);   // {x0,y0,x1,y1}
   const svgRef = useRef(null);
   const panRef = useRef({ startX: 0, startY: 0,
                              originX: 0, originY: 0 });

   const {
      setDocWithHistory, undo, redo, canUndo, canRedo,
   } = useEditorHistory(doc, setDoc);

   const paletteItems = useMemo(
      () => [...EQUIPMENT_PALETTE, ...extraPaletteItems],
      [extraPaletteItems]);

   // ── Load doc — STABLE deps (only primitives) ──
   useEffect(() => {
      if (!projectId || !sheetId) return;
      let alive = true;
      setLoading(true); setErr("");
      const url = `/projects/${projectId}/${docKind}/sheet/${sheetId}/editor-doc`;
      (async () => {
         try {
            const { data } = await api.get(url);
            const raw = data?.doc || null;
            const safe = raw ? {
               ...raw,
               equipment: Array.isArray(raw.equipment) ? raw.equipment : [],
               streams:   Array.isArray(raw.streams)   ? raw.streams   : [],
               instruments: Array.isArray(raw.instruments)
                                 ? raw.instruments : undefined,
            } : null;
            if (alive) setDoc(safe);
         } catch (e) {
            if (alive) setErr(formatErr(e));
         } finally {
            if (alive) setLoading(false);
         }
      })();
      return () => { alive = false; };
   }, [projectId, sheetId, docKind]);

   // ── Save ──
   const handleSave = useCallback(async () => {
      if (!doc) return;
      setSaving(true); setErr("");
      try {
         const payload = {
            equipment: doc.equipment || [],
            streams: doc.streams || [],
            title: doc.title || "",
         };
         if (Array.isArray(doc.instruments)) {
            payload.instruments = doc.instruments;
         }
         const url = `/projects/${projectId}/${docKind}/sheet/${sheetId}/editor-doc`;
         const { data } = await api.put(url, payload);
         onSaved?.(data);
      } catch (e) {
         setErr(formatErr(e));
      } finally {
         setSaving(false);
      }
   }, [doc, projectId, sheetId, docKind, onSaved]);

   // ── Selection helpers ──
   const selectedEq = useMemo(() =>
      (doc?.equipment || []).find((e) => e.id === selection?.id) || null,
   [doc, selection]);
   const selectedStream = useMemo(() =>
      (doc?.streams || []).find((s) => s.id === selection?.id) || null,
   [doc, selection]);

   // ── Delete selection ──
   const handleDelete = useCallback(() => {
      if (!doc) return;
      // Multi-select wins over single selection
      const ids = multiSelect.length > 0
         ? new Set(multiSelect)
         : (selection?.kind === "equipment"
              ? new Set([selection.id]) : null);
      if (ids) {
         setDocWithHistory((d) => ({
            ...d,
            equipment: (d.equipment || []).filter((e) => !ids.has(e.id)),
            streams: (d.streams || []).filter((s) =>
               !ids.has(s.from.eq_id) && !ids.has(s.to.eq_id)),
         }));
         setMultiSelect([]);
         setSelection(null);
         return;
      }
      if (selection?.kind === "stream") {
         setDocWithHistory((d) => ({
            ...d,
            streams: (d.streams || []).filter((s) => s.id !== selection.id),
         }));
         setSelection(null);
      }
   }, [doc, selection, multiSelect, setDocWithHistory]);

   const updateEq = (eq_id, patch) => {
      setDocWithHistory((d) => ({
         ...d,
         equipment: (d.equipment || []).map((e) =>
            e.id === eq_id ? { ...e, ...patch } : e),
      }));
   };
   const updateStream = (s_id, patch) => {
      setDocWithHistory((d) => ({
         ...d,
         streams: (d.streams || []).map((s) =>
            s.id === s_id ? { ...s, ...patch } : s),
      }));
   };

   const eqById = useMemo(() => {
      const m = {};
      (doc?.equipment || []).forEach((e) => { m[e.id] = e; });
      return m;
   }, [doc]);

   const streamPath = (s) => {
      const a = eqById[s.from.eq_id];
      const b = eqById[s.to.eq_id];
      return routeAroundObstacles(a, b, s.from.nozzle_id, s.to.nozzle_id,
                                                doc?.equipment || []);
   };

   const screenToSvg = useCallback((cx, cy) =>
      toSvg(svgRef.current, cx, cy, zoom, pan),
   [zoom, pan]);

   // ── Mouse-wheel zoom (anchored on cursor position) ──
   const onWheel = useCallback((e) => {
      e.preventDefault();
      const svg = svgRef.current;
      if (!svg) return;
      const rect = svg.getBoundingClientRect();
      const cx = e.clientX - rect.left;
      const cy = e.clientY - rect.top;
      const oldZoom = zoom;
      const factor = e.deltaY < 0 ? 1.15 : (1 / 1.15);
      const newZoom = Math.max(0.2, Math.min(5, oldZoom * factor));
      // Keep the cursor world-point fixed under the mouse
      const worldX = cx / oldZoom - pan.x;
      const worldY = cy / oldZoom - pan.y;
      const newPanX = cx / newZoom - worldX;
      const newPanY = cy / newZoom - worldY;
      setZoom(newZoom);
      setPan({ x: newPanX, y: newPanY });
   }, [zoom, pan]);

   useEffect(() => {
      const svg = svgRef.current;
      if (!svg) return;
      // Attach as non-passive so preventDefault works
      svg.addEventListener("wheel", onWheel, { passive: false });
      return () => svg.removeEventListener("wheel", onWheel);
   }, [onWheel]);

   // ── Equipment drag (reposition) ──
   const dragMovedRef = useRef(false);
   const onEqMouseDown = (e, eq) => {
      if (tool !== "select") return;
      e.stopPropagation();
      const pt = screenToSvg(e.clientX, e.clientY);
      dragMovedRef.current = false;
      // If the clicked equipment is NOT in the current multi-selection,
      // collapse to a single-selection drag. Otherwise drag the whole
      // group together.
      const inGroup = multiSelect.includes(eq.id);
      if (!inGroup) {
         if (!(e.shiftKey || e.metaKey || e.ctrlKey)) {
            setMultiSelect([]);
         } else {
            setMultiSelect((s) => [...s, eq.id]);
         }
         setSelection({ kind: "equipment", id: eq.id });
      }
      setDragEq({
         id: eq.id,
         offX: pt.x - eq.x, offY: pt.y - eq.y,
         startX: eq.x, startY: eq.y,
         groupIds: inGroup ? [...multiSelect] : null,
         groupAnchor: { eqX: eq.x, eqY: eq.y },
      });
   };
   const onCanvasMouseMove = (e) => {
      if (dragEq) {
         const pt = screenToSvg(e.clientX, e.clientY);
         const nx = snap(pt.x - dragEq.offX);
         const ny = snap(pt.y - dragEq.offY);
         if (nx !== dragEq.startX || ny !== dragEq.startY) {
            dragMovedRef.current = true;
         }
         if (dragEq.groupIds && dragEq.groupIds.length > 0) {
            // Group drag — compute delta from anchor & shift all
            const dx = nx - dragEq.groupAnchor.eqX;
            const dy = ny - dragEq.groupAnchor.eqY;
            const ids = new Set(dragEq.groupIds);
            setDoc((d) => ({
               ...d,
               equipment: (d.equipment || []).map((e2) =>
                  ids.has(e2.id)
                     ? { ...e2, x: snap(e2.x + dx), y: snap(e2.y + dy) }
                     : e2),
            }));
            // Re-anchor so next move computes delta from current
            dragEq.groupAnchor = { eqX: nx, eqY: ny };
         } else {
            setDoc((d) => ({
               ...d,
               equipment: (d.equipment || []).map((e2) =>
                  e2.id === dragEq.id ? { ...e2, x: nx, y: ny } : e2),
            }));
         }
      } else if (rubberBand) {
         const pt = screenToSvg(e.clientX, e.clientY);
         setRubberBand((r) => ({ ...r, x1: pt.x, y1: pt.y }));
      } else if (isPanning) {
         const dx = e.clientX - panRef.current.startX;
         const dy = e.clientY - panRef.current.startY;
         setPan({
            x: panRef.current.originX + dx / zoom,
            y: panRef.current.originY + dy / zoom,
         });
      }
   };
   const onCanvasMouseDown = (e) => {
      if (tool === "select" && e.target.tagName === "rect"
            && e.target.id === "bg-rect") {
         const pt = screenToSvg(e.clientX, e.clientY);
         if (e.shiftKey || e.metaKey || e.ctrlKey) {
            // Start rubber-band selection
            setRubberBand({ x0: pt.x, y0: pt.y, x1: pt.x, y1: pt.y });
            setSelection(null);
         } else {
            // Pan + clear selection
            setIsPanning(true);
            panRef.current = {
               startX: e.clientX, startY: e.clientY,
               originX: pan.x, originY: pan.y,
            };
            setSelection(null);
            setMultiSelect([]);
         }
      }
   };
   const onCanvasMouseUp = () => {
      if (rubberBand) {
         // Resolve which equipment falls inside the band
         const x0 = Math.min(rubberBand.x0, rubberBand.x1);
         const y0 = Math.min(rubberBand.y0, rubberBand.y1);
         const x1 = Math.max(rubberBand.x0, rubberBand.x1);
         const y1 = Math.max(rubberBand.y0, rubberBand.y1);
         const inside = (doc?.equipment || []).filter((eq) => {
            const cx = eq.x + eq.w / 2;
            const cy = eq.y + eq.h / 2;
            return cx >= x0 && cx <= x1 && cy >= y0 && cy <= y1;
         }).map((eq) => eq.id);
         setMultiSelect(inside);
         setSelection(inside.length === 1
            ? { kind: "equipment", id: inside[0] } : null);
         setRubberBand(null);
      }
      setDragEq(null);
      setIsPanning(false);
      dragMovedRef.current = false;
   };

   const onNozzleClick = (eq, nz) => {
      if (tool !== "line") return;
      if (!linkFrom) {
         setLinkFrom({ eq_id: eq.id, nozzle_id: nz.id });
      } else if (linkFrom.eq_id === eq.id && linkFrom.nozzle_id === nz.id) {
         setLinkFrom(null);
      } else {
         const newStream = {
            id: newId("s"), line_no: "", fluid: "", size_mm: 100,
            from: linkFrom,
            to: { eq_id: eq.id, nozzle_id: nz.id },
         };
         setDocWithHistory((d) => ({ ...d,
            streams: [...(d.streams || []), newStream] }));
         setLinkFrom(null);
         setSelection({ kind: "stream", id: newStream.id });
      }
   };

   const onStreamClick = (s) => {
      if (tool !== "select") return;
      setSelection({ kind: "stream", id: s.id });
   };

   // ── Palette drag-and-drop ──
   const onCanvasDragOver = (e) => {
      if (e.dataTransfer.types?.includes("text/eq-type")) {
         e.preventDefault();
         e.dataTransfer.dropEffect = "copy";
      }
   };
   const onCanvasDrop = (e) => {
      const eqType = e.dataTransfer.getData("text/eq-type");
      if (!eqType) return;
      e.preventDefault();
      const pt = screenToSvg(e.clientX, e.clientY);
      const item = paletteItems.find((p) => p.type === eqType)
         || { type: eqType, tag_prefix: "EQ" };
      const { w, h, nozzles } = defaultNozzlesFor(eqType);
      const existingTags = (doc?.equipment || []).map((eq) => eq.tag);
      const tag = generateTag(item.tag_prefix || "EQ", existingTags);
      const newEq = {
         id: newId("eq"), tag, type: eqType,
         label: item.label || eqType,
         x: snap(pt.x - w / 2), y: snap(pt.y - h / 2),
         w, h, nozzles,
      };
      setDocWithHistory((d) => ({ ...d,
         equipment: [...(d.equipment || []), newEq] }));
      setSelection({ kind: "equipment", id: newEq.id });
      setDraggingType(null);
   };

   // ── Keyboard: Delete, Esc fullscreen ──
   useEffect(() => {
      const h = (e) => {
         const tag = (document.activeElement?.tagName || "").toLowerCase();
         if (tag === "input" || tag === "textarea") return;
         if ((e.key === "Delete" || e.key === "Backspace") && selection) {
            e.preventDefault();
            handleDelete();
         } else if (e.key === "Escape" && fullscreen) {
            setFullscreen(false);
         }
      };
      window.addEventListener("keydown", h);
      return () => window.removeEventListener("keydown", h);
   }, [selection, handleDelete, fullscreen]);

   // ── Fit-to-content on first load ──
   useEffect(() => {
      if (!doc || !doc.equipment?.length) return;
      const xs = doc.equipment.map((e) => e.x);
      const ys = doc.equipment.map((e) => e.y);
      const xMax = Math.max(...doc.equipment.map((e) => e.x + e.w));
      const yMax = Math.max(...doc.equipment.map((e) => e.y + e.h));
      const xMin = Math.min(...xs);
      const yMin = Math.min(...ys);
      const w = xMax - xMin || 800;
      const h = yMax - yMin || 600;
      const fit = Math.min(1400 / (w + 200), 800 / (h + 200), 1);
      setZoom(Math.max(0.4, fit));
      setPan({ x: -xMin + 60, y: -yMin + 40 });
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [doc?.sheet_id]);

   if (loading) {
      return (
         <div style={{ padding: 24, color: COL.dim, ...mono, fontSize: 12 }}>
            <Loader2 size={12} style={{ display: "inline",
               animation: "spin 1s linear infinite", marginRight: 6 }}/>
            Loading {docKind.toUpperCase()} editor…
         </div>
      );
   }
   if (!doc) {
      return (
         <div style={{ padding: 24, color: COL.dim, ...mono, fontSize: 12 }}>
            {err || "No editor document available."}
         </div>
      );
   }

   const eqCount = (doc.equipment || []).length;
   const streamCount = (doc.streams || []).length;

   const shellStyle = fullscreen ? {
      position: "fixed", inset: 0, zIndex: 9999,
      background: COL.bg, color: COL.text, ...mono,
      display: "flex", flexDirection: "column",
   } : {
      height: "100%", display: "flex", flexDirection: "column",
      ...mono, color: COL.text, background: COL.bg,
   };

   return (
      <div data-testid={testidPrefix} style={shellStyle}>
         {/* Toolbar */}
         <div style={{ borderBottom: `1px solid ${COL.border}`,
                          padding: "8px 12px", display: "flex",
                          alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <div style={{ fontSize: 12, fontWeight: 700,
                              letterSpacing: 0.5, marginRight: 12 }}>
               {titlePrefix} · {sheetId} {sheetTitle && (
                  <span style={{ color: COL.dim, fontWeight: 400, marginLeft: 6 }}>
                     {sheetTitle}
                  </span>
               )}
            </div>
            <ToolBtn active={tool === "select"} onClick={() => setTool("select")}
                  icon={MousePointer2} label="Select" testid="tool-select"/>
            <ToolBtn active={tool === "line"} onClick={() => { setTool("line"); setLinkFrom(null); }}
                  icon={Link2} label="Add Line" testid="tool-line"/>
            <ToolBtn onClick={handleDelete} icon={Trash2} label="Delete"
                  disabled={!selection} testid="tool-delete" danger/>
            <ToolBtn onClick={undo} icon={Undo2} label="Undo"
                  disabled={!canUndo} testid="tool-undo"/>
            <ToolBtn onClick={redo} icon={Redo2} label="Redo"
                  disabled={!canRedo} testid="tool-redo"/>
            <div style={{ marginLeft: "auto", display: "flex",
                              alignItems: "center", gap: 6 }}>
               <span style={{ fontSize: 10, color: COL.dim }}>
                  {eqCount} eq · {streamCount} streams
               </span>
               <ToolBtn icon={ZoomIn} label="+"
                  onClick={() => setZoom((z) => Math.min(z * 1.2, 4))}
                  testid="tool-zoom-in"/>
               <ToolBtn icon={ZoomOut} label="−"
                  onClick={() => setZoom((z) => Math.max(z / 1.2, 0.3))}
                  testid="tool-zoom-out"/>
               <ToolBtn icon={RotateCcw} label="Reset"
                  onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); }}
                  testid="tool-reset"/>
               <ToolBtn icon={fullscreen ? Minimize2 : Maximize2}
                  label={fullscreen ? "Exit" : "Fullscreen"}
                  onClick={() => setFullscreen((v) => !v)}
                  testid="tool-fullscreen"/>
               <button data-testid={`${testidPrefix}-save`}
                       onClick={handleSave} disabled={saving}
                       style={{ background: saving ? COL.dim : COL.spark,
                                    color: "#020617",
                                    border: "none", padding: "6px 12px",
                                    borderRadius: 3, fontWeight: 700,
                                    fontSize: 11, cursor: "pointer",
                                    ...mono }}>
                  {saving ? "Saving…" : <><Save size={11}
                     style={{ marginRight: 4, marginBottom: -1 }}/>SAVE</>}
               </button>
               {onRequestClose && (
                  <button data-testid={`${testidPrefix}-close`}
                          onClick={onRequestClose}
                          title="Close editor"
                          style={{ background: "transparent",
                                       color: COL.dim, border: "none",
                                       padding: "6px 8px", cursor: "pointer",
                                       ...mono }}>
                     <XIcon size={14}/>
                  </button>
               )}
            </div>
         </div>

         {err && (
            <div data-testid={`${testidPrefix}-error`}
                  style={{ background: "#7f1d1d", color: "#fee2e2",
                              padding: 8, fontSize: 11 }}>
               <AlertCircle size={11} style={{ display: "inline", marginRight: 4 }}/>
               {err}
            </div>
         )}
         {tool === "line" && (
            <div data-testid={`${testidPrefix}-hint`}
                  style={{ background: "rgba(34, 211, 238, 0.1)",
                              color: COL.spark, padding: 8, fontSize: 11 }}>
               Click a nozzle to start a connection.
               {linkFrom && " Now click another nozzle to complete."}
            </div>
         )}
         {tool === "select" && multiSelect.length > 1 && (
            <div data-testid={`${testidPrefix}-multi-hint`}
                  style={{ background: "rgba(245, 158, 11, 0.12)",
                              color: COL.warn, padding: "6px 10px",
                              fontSize: 11 }}>
               {multiSelect.length} items selected — drag any one to
               move the group, Delete to remove all.
            </div>
         )}
         {tool === "select" && !rubberBand && multiSelect.length <= 1 && (
            <div style={{ background: "rgba(148, 163, 184, 0.05)",
                              color: COL.dim, padding: "4px 10px",
                              fontSize: 10 }}>
               Tip: Shift-drag the background to rubber-band select
               multiple equipment.
            </div>
         )}

         {/* Palette + Canvas + Properties panel */}
         <div style={{ flex: 1, display: "flex", overflow: "hidden",
                            minHeight: 0 }}>
            <EquipmentPalette items={paletteItems}
                              draggingType={draggingType}
                              setDraggingType={setDraggingType}/>
            <div style={{ flex: 1, position: "relative", overflow: "hidden",
                              cursor: isPanning ? "grabbing"
                                              : (draggingType ? "copy" : "default") }}
                  onDragOver={onCanvasDragOver}
                  onDrop={onCanvasDrop}
                  data-testid={`${testidPrefix}-dropzone`}>
               <svg ref={svgRef} data-testid={`${testidPrefix}-canvas`}
                     width="100%" height="100%"
                     viewBox="0 0 1600 900"
                     onMouseDown={onCanvasMouseDown}
                     onMouseMove={onCanvasMouseMove}
                     onMouseUp={onCanvasMouseUp}
                     onMouseLeave={onCanvasMouseUp}
                     style={{ background: COL.bg }}>
                  <defs>
                     <pattern id="grid" width={GRID} height={GRID}
                                patternUnits="userSpaceOnUse">
                        <path d={`M ${GRID} 0 L 0 0 0 ${GRID}`} fill="none"
                                stroke={COL.border} strokeWidth="0.5"/>
                     </pattern>
                  </defs>
                  <g transform={`translate(${pan.x * zoom} ${pan.y * zoom}) scale(${zoom})`}>
                     <rect id="bg-rect" x="-2000" y="-2000"
                             width="6000" height="6000" fill="url(#grid)"/>
                     {(doc.streams || []).map((s) => {
                        const path = streamPath(s);
                        if (!path) return null;
                        const isSelected = selection?.kind === "stream"
                              && selection.id === s.id;
                        return (
                           <g key={s.id}
                                data-testid={`${testidPrefix}-stream-${s.id}`}>
                              <path d={path} fill="none"
                                       stroke={isSelected ? COL.warn : COL.spark}
                                       strokeWidth={isSelected ? 3 : 2}
                                       style={{ cursor: "pointer" }}
                                       onClick={() => onStreamClick(s)}/>
                              {s.line_no && (() => {
                                 const a = eqById[s.from.eq_id];
                                 const b = eqById[s.to.eq_id];
                                 if (!a || !b) return null;
                                 const na = resolveNozzle(a, s.from.nozzle_id);
                                 const nb = resolveNozzle(b, s.to.nozzle_id);
                                 const mx = (na.x + nb.x) / 2;
                                 const my = Math.min(na.y, nb.y) - 4;
                                 return (
                                    <text x={mx} y={my} fontSize="9"
                                            fill={COL.spark}
                                            textAnchor="middle">
                                       {s.line_no}
                                    </text>
                                 );
                              })()}
                           </g>
                        );
                     })}
                     {(doc.equipment || []).map((eq) => {
                        const isSelected = selection?.kind === "equipment"
                              && selection.id === eq.id;
                        const isMulti = multiSelect.includes(eq.id);
                        const strokeColor = isSelected || isMulti
                              ? COL.warn : COL.spark;
                        const strokeWidth = isSelected || isMulti ? 3 : 2;
                        return (
                           <g key={eq.id} transform={`translate(${eq.x} ${eq.y})`}
                                data-testid={`${testidPrefix}-eq-${eq.id}`}
                                onMouseDown={(e) => onEqMouseDown(e, eq)}
                                style={{ cursor: tool === "select"
                                              ? "move" : "default" }}>
                              <EquipmentSymbol type={eq.type}
                                                    w={eq.w} h={eq.h}
                                                    stroke={strokeColor}
                                                    strokeWidth={strokeWidth}
                                                    fill={COL.panel}/>
                              <text x={eq.w / 2} y={eq.h / 2}
                                       fontSize="12" fontWeight="700"
                                       fill={COL.text}
                                       textAnchor="middle">{eq.tag}</text>
                              <text x={eq.w / 2} y={eq.h / 2 + 14}
                                       fontSize="9" fill={COL.dim}
                                       textAnchor="middle">
                                 {eq.label || eq.type}
                              </text>
                              {(eq.nozzles || []).map((nz) => {
                                 const npos = resolveNozzle(
                                    { ...eq, x: 0, y: 0 }, nz.id);
                                 const isLinking = linkFrom
                                       && linkFrom.eq_id === eq.id
                                       && linkFrom.nozzle_id === nz.id;
                                 return (
                                    <circle key={nz.id}
                                             cx={npos.x} cy={npos.y} r="4"
                                             fill={isLinking ? COL.warn
                                                      : (tool === "line"
                                                         ? COL.ok : COL.spark)}
                                             stroke="#020617" strokeWidth="1"
                                             data-testid={`${testidPrefix}-nz-${eq.id}-${nz.id}`}
                                             style={{ cursor: tool === "line"
                                                         ? "crosshair" : "default" }}
                                             onClick={(e) => {
                                                e.stopPropagation();
                                                onNozzleClick(eq, nz);
                                             }}/>
                                 );
                              })}
                           </g>
                        );
                     })}
                     {rubberBand && (
                        <rect data-testid={`${testidPrefix}-rubberband`}
                              x={Math.min(rubberBand.x0, rubberBand.x1)}
                              y={Math.min(rubberBand.y0, rubberBand.y1)}
                              width={Math.abs(rubberBand.x1 - rubberBand.x0)}
                              height={Math.abs(rubberBand.y1 - rubberBand.y0)}
                              fill="rgba(34,211,238,0.10)"
                              stroke={COL.spark} strokeDasharray="4 4"
                              strokeWidth="1" pointerEvents="none"/>
                     )}
                  </g>
               </svg>
            </div>
            <div data-testid={`${testidPrefix}-props`}
                  style={{ width: 280,
                              borderLeft: `1px solid ${COL.border}`,
                              padding: 14, overflow: "auto",
                              background: COL.panel, fontSize: 11 }}>
               <div style={{ fontSize: 11, fontWeight: 700,
                                  letterSpacing: 0.5, marginBottom: 12 }}>
                  PROPERTIES
               </div>
               {!selection && (
                  <div style={{ color: COL.dim, fontStyle: "italic" }}>
                     Select an equipment or stream to edit its properties,
                     or drag an item from the palette to add new equipment.
                  </div>
               )}
               {selectedEq && (
                  <>
                     <PropField label="Tag" value={selectedEq.tag}
                        onChange={(v) => updateEq(selectedEq.id, { tag: v })}
                        testid="prop-eq-tag"/>
                     <PropField label="Type" value={selectedEq.type}
                        onChange={(v) => updateEq(selectedEq.id, { type: v })}
                        testid="prop-eq-type"/>
                     <PropField label="Label" value={selectedEq.label || ""}
                        onChange={(v) => updateEq(selectedEq.id, { label: v })}
                        testid="prop-eq-label"/>
                     <PropField label="X" type="number" value={selectedEq.x}
                        onChange={(v) => updateEq(selectedEq.id,
                           { x: parseFloat(v) || 0 })}
                        testid="prop-eq-x"/>
                     <PropField label="Y" type="number" value={selectedEq.y}
                        onChange={(v) => updateEq(selectedEq.id,
                           { y: parseFloat(v) || 0 })}
                        testid="prop-eq-y"/>
                     <PropField label="Width" type="number" value={selectedEq.w}
                        onChange={(v) => updateEq(selectedEq.id,
                           { w: parseFloat(v) || 60 })}
                        testid="prop-eq-w"/>
                     <PropField label="Height" type="number" value={selectedEq.h}
                        onChange={(v) => updateEq(selectedEq.id,
                           { h: parseFloat(v) || 60 })}
                        testid="prop-eq-h"/>
                  </>
               )}
               {selectedStream && (
                  <>
                     <PropField label="Stream ID" value={selectedStream.id}
                        readOnly testid="prop-s-id"/>
                     <PropField label="Line No" value={selectedStream.line_no}
                        onChange={(v) => updateStream(selectedStream.id,
                           { line_no: v })}
                        testid="prop-s-line-no"/>
                     <PropField label="Fluid" value={selectedStream.fluid}
                        onChange={(v) => updateStream(selectedStream.id,
                           { fluid: v })}
                        testid="prop-s-fluid"/>
                     <PropField label="Size (mm)" type="number"
                        value={selectedStream.size_mm}
                        onChange={(v) => updateStream(selectedStream.id,
                           { size_mm: parseFloat(v) || 100 })}
                        testid="prop-s-size"/>
                  </>
               )}
            </div>
         </div>
      </div>
   );
}

function ToolBtn({ active, onClick, icon: Icon, label, disabled, danger,
                       testid }) {
   const bg = disabled
      ? "rgba(30, 41, 59, 0.3)"
      : (active ? COL.active : (danger ? "rgba(239, 68, 68, 0.15)"
                                              : "rgba(34, 211, 238, 0.1)"));
   const color = disabled
      ? "rgba(148, 163, 184, 0.5)"
      : (active ? "white" : (danger ? COL.err : COL.text));
   return (
      <button type="button" data-testid={testid} onClick={onClick}
              disabled={disabled}
              title={label}
              style={{ display: "flex", alignItems: "center", gap: 4,
                          background: bg, color, border: "none",
                          padding: "6px 10px", borderRadius: 3,
                          fontSize: 11, fontWeight: active ? 700 : 400,
                          cursor: disabled ? "not-allowed" : "pointer",
                          ...mono }}>
         <Icon size={12}/> <span>{label}</span>
      </button>
   );
}

function PropField({ label, value, type = "text", onChange, readOnly,
                         testid }) {
   return (
      <div style={{ marginBottom: 8 }}>
         <div style={{ color: COL.dim, fontSize: 9,
                          letterSpacing: 0.5, marginBottom: 3,
                          textTransform: "uppercase" }}>
            {label}
         </div>
         <input data-testid={testid}
                value={value ?? ""} type={type} readOnly={readOnly}
                onChange={(e) => onChange?.(e.target.value)}
                style={{ width: "100%", boxSizing: "border-box",
                            padding: "4px 8px", fontSize: 11,
                            background: readOnly ? "transparent"
                                                            : COL.bg,
                            color: COL.text,
                            border: `1px solid ${COL.border}`,
                            borderRadius: 3, ...mono }}/>
      </div>
   );
}
